import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { manualTradingApi, analystReviewsApi, agentSignalsApi, personalAdvisorApi, scoutApi, AdvisorAnalysisRow, ScoutScheduledRun } from '../../services/api';
import { AnalystReview, AgentSignal } from '../../types/monitor.types';
import { TimePeriod, DateRange } from '../../types/analytics.types';
import { getCurrentTradingDay, getCurrentTradingWeek } from '../../utils/marketHours';
import { parseUTC } from '../../utils/formatters';
import TodaysTrades from '../LiveMonitor/components/TodaysTrades';
import AnalystReviews from '../LiveMonitor/components/AnalystReviews';
import AgentSignals from '../LiveMonitor/components/AgentSignals';
import AdvisorAnalyses from '../LiveMonitor/components/AdvisorAnalyses';
import '../LiveMonitor/LiveMonitor.css';
import './Historical.css';

// LLM-related tabs (Entry Reviews / Triggers / Exit Reviews) live on the
// dedicated /llm page now. Historical only hosts the legacy ML pipeline views.

const MANUAL_ACCOUNT_ID = '299087495130306062';
const DEFAULT_FETCH_LIMIT = 1000;

type HistoricalTab = 'trades' | 'blocked' | 'reviews' | 'agent' | 'advisor';

function computeDateRange(period: TimePeriod, custom: DateRange): { start: Date; end: Date } | null {
  if (period === 'all_time') return null;
  if (period === 'today') {
    const { start, end } = getCurrentTradingDay();
    return { start, end };
  }
  if (period === 'this_week') {
    const { start, end } = getCurrentTradingWeek();
    return { start, end };
  }
  if (period === 'this_month') {
    const now = new Date();
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0));
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0, 23, 59, 59));
    return { start, end };
  }
  // custom
  const start = custom.from ? new Date(custom.from + 'T00:00:00Z') : null;
  const end = custom.to ? new Date(custom.to + 'T23:59:59Z') : null;
  if (!start && !end) return null;
  return {
    start: start ?? new Date(0),
    end: end ?? new Date(),
  };
}

function inRange(iso: string | null | undefined, range: { start: Date; end: Date } | null): boolean {
  if (!iso) return false;
  if (!range) return true;
  const t = parseUTC(iso);
  return t >= range.start && t <= range.end;
}

const Historical: React.FC = () => {
  const [activeTab, setActiveTab] = useState<HistoricalTab>('trades');
  const [period, setPeriod] = useState<TimePeriod>('today');
  const [dateRange, setDateRange] = useState<DateRange>({ from: '', to: '' });

  const [allTrades, setAllTrades] = useState<any[]>([]);
  const [allReviews, setAllReviews] = useState<AnalystReview[]>([]);
  const [allAgentSignals, setAllAgentSignals] = useState<AgentSignal[]>([]);
  const [allAdvisorAnalyses, setAllAdvisorAnalyses] = useState<AdvisorAnalysisRow[]>([]);
  const [allScoutRuns, setAllScoutRuns] = useState<ScoutScheduledRun[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [trades, reviews, signals, advisorRows, scoutRuns] = await Promise.all([
        manualTradingApi.getTrades().catch(() => []),
        analystReviewsApi.getReviews(DEFAULT_FETCH_LIMIT).catch(() => []),
        agentSignalsApi.getSignals(DEFAULT_FETCH_LIMIT).catch(() => []),
        personalAdvisorApi.getAnalyses(DEFAULT_FETCH_LIMIT).catch(() => [] as AdvisorAnalysisRow[]),
        scoutApi.getScheduledRuns(500).catch(() => [] as ScoutScheduledRun[]),
      ]);
      setAllTrades(trades);
      setAllReviews(reviews);
      setAllAgentSignals(signals);
      setAllAdvisorAnalyses(advisorRows);
      setAllScoutRuns(scoutRuns);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const range = useMemo(() => computeDateRange(period, dateRange), [period, dateRange]);

  const filteredTrades = useMemo(() => {
    const closed = allTrades.filter((t: any) => {
      const accountId = t.account_id || t.accountId || '';
      if (accountId === MANUAL_ACCOUNT_ID) return false;
      if (t.status !== 'CLOSED' || t.profit_loss == null) return false;
      return inRange(t.exit_time, range);
    });
    closed.sort((a: any, b: any) => parseUTC(b.exit_time).getTime() - parseUTC(a.exit_time).getTime());
    return closed;
  }, [allTrades, range]);

  const filteredReviews = useMemo(
    () => allReviews.filter(r => inRange(r.triggeredAt, range)),
    [allReviews, range]
  );

  const filteredBlocked = useMemo(
    () => filteredReviews.filter(r => r.verdict === 'BLOCK'),
    [filteredReviews]
  );

  const filteredAgent = useMemo(
    () => allAgentSignals.filter(s => inRange(s.generatedAt, range)),
    [allAgentSignals, range]
  );

  const filteredAdvisorAnalyses = useMemo(
    () => allAdvisorAnalyses.filter(a => inRange(a.created_at, range)),
    [allAdvisorAnalyses, range]
  );

  const filteredScoutRuns = useMemo(
    () => allScoutRuns.filter(r => inRange(r.ran_at, range)),
    [allScoutRuns, range]
  );

  const handleToday = () => {
    const { start, end } = getCurrentTradingDay();
    setPeriod('today');
    setDateRange({ from: start.toISOString().slice(0, 10), to: end.toISOString().slice(0, 10) });
  };
  const handleWeek = () => {
    const { start, end } = getCurrentTradingWeek();
    setPeriod('this_week');
    setDateRange({ from: start.toISOString().slice(0, 10), to: end.toISOString().slice(0, 10) });
  };
  const handleMonth = () => {
    const now = new Date();
    const from = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const to = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    setPeriod('this_month');
    setDateRange({ from, to });
  };
  const handleAll = () => {
    setPeriod('all_time');
    setDateRange({ from: '', to: '' });
  };

  const rangeLabel = useMemo(() => {
    switch (period) {
      case 'today': return "Today's Closed Trades";
      case 'this_week': return "This Week's Closed Trades";
      case 'this_month': return "This Month's Closed Trades";
      case 'all_time': return 'All Closed Trades';
      case 'custom': return 'Closed Trades (custom range)';
    }
  }, [period]);

  return (
    <div className="historical-page">
      <div className="historical-header">
        <h2 className="historical-title">Historical</h2>
        <button className="reviews-refresh-btn" onClick={fetchAll} disabled={loading}>
          {loading ? 'Loading...' : '↻ Refresh'}
        </button>
      </div>

      <div className="historical-filter">
        <div className="historical-filter-buttons">
          <button className={`filter-btn ${period === 'all_time' ? 'active' : ''}`} onClick={handleAll}>All Time</button>
          <button className={`filter-btn ${period === 'this_month' ? 'active' : ''}`} onClick={handleMonth}>This Month</button>
          <button className={`filter-btn ${period === 'this_week' ? 'active' : ''}`} onClick={handleWeek}>This Week</button>
          <button className={`filter-btn ${period === 'today' ? 'active' : ''}`} onClick={handleToday}>Today</button>
        </div>
        <div className="historical-filter-dates">
          <label className="filter-date-label">
            From
            <input
              type="date"
              className="filter-date-input"
              value={dateRange.from}
              onChange={e => { setPeriod('custom'); setDateRange(prev => ({ ...prev, from: e.target.value })); }}
            />
          </label>
          <label className="filter-date-label">
            To
            <input
              type="date"
              className="filter-date-input"
              value={dateRange.to}
              onChange={e => { setPeriod('custom'); setDateRange(prev => ({ ...prev, to: e.target.value })); }}
            />
          </label>
        </div>
      </div>

      <div className="monitor-tabs">
        <button
          className={`monitor-tab ${activeTab === 'trades' ? 'tab-active' : ''}`}
          onClick={() => setActiveTab('trades')}
        >
          Trades <span className="tab-count">{filteredTrades.length}</span>
        </button>
        <button
          className={`monitor-tab ${activeTab === 'blocked' ? 'tab-active' : ''}`}
          onClick={() => setActiveTab('blocked')}
        >
          Blocked <span className="tab-count tab-count-blocked">{filteredBlocked.length}</span>
        </button>
        <button
          className={`monitor-tab ${activeTab === 'reviews' ? 'tab-active' : ''}`}
          onClick={() => setActiveTab('reviews')}
        >
          Analyst Reviews <span className="tab-count tab-count-reviews">{filteredReviews.length}</span>
        </button>
        <button
          className={`monitor-tab ${activeTab === 'agent' ? 'tab-active' : ''}`}
          onClick={() => setActiveTab('agent')}
        >
          Discovery Agent <span className="tab-count tab-count-agent">{filteredAgent.length}</span>
        </button>
        <button
          className={`monitor-tab ${activeTab === 'advisor' ? 'tab-active' : ''}`}
          onClick={() => setActiveTab('advisor')}
        >
          Advisor <span className="tab-count tab-count-reviews">{filteredAdvisorAnalyses.length}</span>
        </button>
      </div>

      {activeTab === 'trades' && (
        <TodaysTrades trades={filteredTrades} showDate={period !== 'today'} title={rangeLabel} />
      )}
      {activeTab === 'blocked' && (
        <AnalystReviews reviews={filteredBlocked} loading={loading} onRefresh={fetchAll} showDate={period !== 'today'} />
      )}
      {activeTab === 'reviews' && (
        <AnalystReviews reviews={filteredReviews} loading={loading} onRefresh={fetchAll} showDate={period !== 'today'} />
      )}
      {activeTab === 'agent' && (
        <AgentSignals signals={filteredAgent} loading={loading} onRefresh={fetchAll} showDate={period !== 'today'} />
      )}
      {activeTab === 'advisor' && (
        <AdvisorAnalyses analyses={filteredAdvisorAnalyses} loading={loading} onRefresh={fetchAll} showDate={period !== 'today'} />
      )}
    </div>
  );
};

export default Historical;
